import { FlameDetectionRange } from "@domain/product/flame-detection-range";
import { Product } from "@domain/product/product";
import { ProductConfiguration } from "@domain/project/configurations/product-configuration";
import { ProjectEventType } from "@domain/project/project-event";
import { FlameDetectorType } from "@project/flame-detector-configuration/flame-detector-type";
import { ProductItem } from "@project/product-item";
import { Expose, Type } from "class-transformer";
import { IsArray, IsOptional, IsString, ValidateNested } from "class-validator";
import { v4 as uuidv4 } from "uuid";
import { ScopedProject } from "../project-guards";

export class FlameDetectorConfiguration extends ProductConfiguration {
  private static readonly NAME_REGEX = /((?:\bPolytron\b|\bPIR\b)\s\d+)/g;

  @IsString()
  public variantId: string;

  @Expose()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => ProductItem)
  attachments: ProductItem[];

  @IsString()
  @IsOptional()
  public substanceId?: string;

  @IsString()
  @IsOptional()
  public family?: string;

  @ValidateNested()
  @Type(() => FlameDetectionRange)
  public detectionRange!: FlameDetectionRange;

  constructor(
    id: string,
    name: string,
    project: ScopedProject,
    variantId: string,
    attachments: ProductItem[],
    detectionRange: FlameDetectionRange = FlameDetectionRange.create("", 0),
    substanceId?: string,
    family?: string,
  ) {
    super(id, name, project);
    this.variantId = variantId;
    this.attachments = attachments;
    this.detectionRange = detectionRange;
    this.substanceId = substanceId;
    this.family = family;
  }

  static create(
    name: string,
    project: ScopedProject,
    variantId: string,
    attachments: ProductItem[],
    detectionRange?: FlameDetectionRange,
    substanceId?: string,
    family?: string,
  ): FlameDetectorConfiguration {
    return new FlameDetectorConfiguration(uuidv4(), name, project, variantId, attachments, detectionRange, substanceId, family);
  }

  override get productDataName(): string {
    return FlameDetectorConfiguration.getProductDataName(this.name);
  }

  countPlacedProducts(project: ScopedProject = this.project): number {
    return project ? project.countPlacedFlameDetectors(this) : 0;
  }

  update(
    substanceId: string,
    flameDetector: Product,
    attachments: ProductItem[],
    detectionRange: FlameDetectionRange,
    family?: FlameDetectorType,
  ) {
    this.substanceId = substanceId;
    this.name = flameDetector.name;
    this.family = family?.key;
    this.variantId = flameDetector.id;
    this.attachments = attachments;
    this.detectionRange = detectionRange;
    this.project.publishUpdate(ProjectEventType.FLAME_DETECTOR_CONFIGURATION_UPDATED, this);
  }

  override get mainProductId(): string {
    return this.variantId;
  }

  override get configuredProductIds(): string[] {
    return this.attachmentIds.concat(this.variantId);
  }

  get attachmentIds(): string[] {
    return this.attachments.map((attachment) => attachment.productId);
  }

  get defaultCoverageAreaLength(): number {
    if (this.detectionRange.detectionRangeDefault) {
      return this.detectionRange.detectionRangeDefault;
    }
    return this.detectionRange.detectionRangeHigh ?? this.detectionRange.detectionRangeLow ?? 0;
  }

  static getProductDataName(name: string) {
    const regexMatch = name.match(FlameDetectorConfiguration.NAME_REGEX);
    if (!regexMatch || !regexMatch.length) {
      return "";
    }
    return regexMatch[0].replace(" ", "_");
  }
}
